import React, { useCallback, useEffect, useState } from 'react';
import { useProductScanner, Product } from '../hooks/useProductScanner';
const NAMES = ['Mary', 'James', 'Olivia', 'Daniel', 'Emma', 'Andrew', 'Lucy', 'Matthew', 'Sophia', 'Sebastian', 'Paula', 'Nicholas', 'Florence', 'Thomas', 'Caroline'];
const CITIES = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'Denver', 'Seattle', 'Boston', 'Toronto', 'London', 'Dublin', 'Sydney'];
interface ToastData {
  name: string;
  city: string;
  timeAgo: number;
  product: Product;
}
export function RecentPurchaseToast() {
  const products = useProductScanner();
  const [isVisible, setIsVisible] = useState(false);
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  const generateToastData = useCallback(() => {
    if (products.length === 0) return null;
    return {
      name: getRandomItem(NAMES),
      city: getRandomItem(CITIES),
      timeAgo: getRandomInt(1, 15),
      product: getRandomItem(products)
    };
  }, [products]);
  useEffect(() => {
    if (products.length === 0) return;
    let showTimeout: ReturnType<typeof setTimeout>;
    let hideTimeout: ReturnType<typeof setTimeout>;
    const scheduleNextToast = (isFirst: boolean = false) => {
      // First toast appears after 8-12s, subsequent ones after 15-25s
      const delay = isFirst ? getRandomInt(8000, 12000) : getRandomInt(15000, 25000);
      showTimeout = setTimeout(() => {
        const newData = generateToastData();
        if (newData) {
          setToastData(newData);
          setIsVisible(true);
          // Hide after 5 seconds
          hideTimeout = setTimeout(() => {
            setIsVisible(false);
            // Schedule the next one after hiding
            scheduleNextToast(false);
          }, 5000);
        } else {
          scheduleNextToast(false);
        }
      }, delay);
    };
    scheduleNextToast(true);
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [products, generateToastData]);
  if (!toastData) return null;
  return <div className={`fixed bottom-3 left-3 right-3 md:right-auto md:left-5 md:bottom-5 z-50 transition-[transform,opacity] duration-300 ease-out ${isVisible ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-[120%] opacity-0 pointer-events-none'}`} data-id="element-255" data-mp="l0e35enk46i">
      <div className="bg-[#150025]/95 backdrop-blur-sm border border-[rgba(194,0,111,0.3)] rounded-lg md:rounded-xl py-2 px-3 md:py-3 md:px-4 md:max-w-[320px] shadow-[0_8px_30px_rgba(0,0,0,0.5),0_0_20px_rgba(194,0,111,0.15)] relative overflow-hidden" data-id="element-256" data-mp="390tt1ftc57lg">
        <div className="flex items-center gap-2.5 md:gap-3" data-id="element-257" data-mp="2gbd3cpx3o4sx">
          {/* Thumbnail */}
          <div className="shrink-0" data-id="element-258" data-mp="2l9oc0xbns99k">
            <img src={toastData.product.thumbnailUrl} alt={toastData.product.title} className="w-8 h-8 md:w-10 md:h-10 rounded-md object-cover border border-white/10" data-id="element-259" data-mp="2l8ms03co0cfp" />
          </div>
          {/* Content — compact single flow */}
          <div className="flex-1 min-w-0" data-id="element-260" data-mp="th9ldorjyyfx">
            <p className="text-white/80 text-[0.7rem] md:text-xs leading-tight truncate" data-id="element-261" data-mp="oglk8a9m117z">
              <span className="font-heading font-bold text-white" data-id="element-262" data-mp="jgqvoddb4nq3">
                {toastData.name}
              </span>
              {' from '}
              {toastData.city}
              {' just bought'}
            </p>
            <div className="flex items-center justify-between gap-2 mt-0.5" data-id="element-263" data-mp="apqj5k5croey">
              <span className="text-magenta-bright font-heading font-bold text-[0.75rem] md:text-sm truncate" data-id="element-264" data-mp="5q44r7dsg24k">
                {toastData.product.title}
              </span>
              <span className="text-gold font-heading font-bold text-[0.75rem] md:text-sm shrink-0" data-id="element-265" data-mp="sc1xibluvll">
                {toastData.product.priceText}
              </span>
            </div>
            <div className="flex items-center justify-between mt-0.5" data-id="element-266" data-mp="1vvqw8cvvdpon">
              <span className="text-green-400 text-[0.6rem] md:text-[0.7rem] font-medium flex items-center gap-0.5" data-id="element-267" data-mp="20rfpjyhaqaci">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-268" data-mp="1m3kuwcjq9vws">
                  <polyline points="20 6 9 17 4 12" data-id="element-269" data-mp="1373889jqkm7e"></polyline>
                </svg>
                Verified
              </span>
              <span className="text-white/30 text-[0.6rem] md:text-[0.65rem]" data-id="element-270" data-mp="2xj9jf8cmr2zp">
                {toastData.timeAgo} min ago
              </span>
            </div>
          </div>
          {/* Close button */}
          <button type="button" onClick={e => {
          e.stopPropagation();
          setIsVisible(false);
        }} className="shrink-0 text-white/30 hover:text-white/60 transition-colors p-0.5" aria-label="Close" data-id="element-271" data-mp="qu7urcj4try5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-272" data-mp="vu3wt689r9b2">
              <line x1="18" y1="6" x2="6" y2="18" data-id="element-273" data-mp="1jhc23o5p6id3"></line>
              <line x1="6" y1="6" x2="18" y2="18" data-id="element-274" data-mp="1eld89l1lo85a"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>;
}